import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Row, Col, Tag, Avatar, Space, Button, Divider,
  Table, Tooltip, message, Skeleton, Badge, Tabs,
} from 'antd';
import {
  CarOutlined, UserOutlined, HomeOutlined, ArrowLeftOutlined,
  CalendarOutlined, ToolOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, FileTextOutlined,
  EnvironmentOutlined, PhoneOutlined, MailOutlined,
  FileProtectOutlined, PictureOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';
import FotoUpload from '../components/FotoUpload';
import Footer from '../components/Footer';

const TIPO_ICONO = {
  camion: '🚛', camioneta: '🚙', auto: '🚗', moto: '🏍️',
  tractor: '🚜', barco: '⛵', avion: '✈️',
};

const ESTADO_CONFIG = {
  ACTIVO:     { color: 'warning',  icon: <ClockCircleOutlined />,   label: 'Activo'      },
  COMPLETADO: { color: 'success',  icon: <CheckCircleOutlined />,   label: 'Completado'  },
  CANCELADO:  { color: 'error',    icon: <CloseCircleOutlined />,   label: 'Cancelado'   },
};

const SERVICIO_COLOR = {
  RASTREO:        hssColors.primary,
  VISION:         '#8e44ad',
  RASTREO_VISION: '#16a085',
};

const FOTOS_CONFIG = [
  { tipo: 'vehiculo',     label: 'Frente / Principal' },
  { tipo: 'atras',        label: 'Parte trasera'      },
  { tipo: 'izquierda',    label: 'Lado conductor'     },
  { tipo: 'derecha',      label: 'Lado acompañante'   },
  { tipo: 'cedula_verde', label: 'Documentación (frente)' },
  { tipo: 'cedula_atras', label: 'Documentación (dorso)'  },
];

const VehiculoDetalle = () => {
  const { chapa } = useParams();
  const navigate = useNavigate();
  const [vehiculo, setVehiculo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const mapaTabDivRef      = useRef(null);
  const mapaTabInstanceRef = useRef(null);
  const [gpsPos, setGpsPos] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/vehiculos/${chapa}`);
      setVehiculo(res.data);
    } catch {
      message.error('No se pudo cargar el vehículo');
    } finally {
      setLoading(false);
    }
  }, [chapa]);

  useEffect(() => { cargar(); }, [cargar]);

  // Fetch posición GPS
  useEffect(() => {
    axiosInstance.get(`/tracking/ubicacion/${chapa}`)
      .then(r => { if (r.data) setGpsPos(r.data); })
      .catch(() => {});
  }, [chapa]);

  // Init mapa de la pestaña Mapa (lazy: solo cuando se abre esa pestaña)
  useEffect(() => {
    if (activeTab !== 'mapa') return;
    const L = window.L;
    if (!L || !gpsPos) return;
    const lat = parseFloat(gpsPos.lat);
    const lng = parseFloat(gpsPos.lng);
    if (!lat && !lng) return;

    if (mapaTabInstanceRef.current) {
      setTimeout(() => mapaTabInstanceRef.current?.invalidateSize(), 80);
      return;
    }

    // Esperar a que Ant Design haga visible el panel antes de que Leaflet calcule dimensiones
    const timer = setTimeout(() => {
    if (!mapaTabDivRef.current) return;

    const map = L.map(mapaTabDivRef.current, { center: [lat, lng], zoom: 15, zoomControl: true });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap', maxZoom: 19,
    }).addTo(map);

    const dtStr  = (gpsPos.dt_tracker || '').replace(' ', 'T');
    const online = dtStr && (Date.now() - new Date(dtStr).getTime()) < 3600000;
    const moving = parseFloat(gpsPos.speed || 0) > 0;
    const color  = !online ? '#9e9e9e' : moving ? '#4caf50' : '#f44336';
    const spd    = Math.round(parseFloat(gpsPos.speed || 0));
    const acc    = gpsPos.params?.acc === '1' || gpsPos.params?.acc === 1
                || gpsPos.params?.io239 === '1' || gpsPos.params?.io239 === 1;

    const raw  = parseFloat(gpsPos.params?.acm0 || gpsPos.params?.io66 || 0);
    const volt = raw > 100 ? (raw / 1000).toFixed(1) : raw > 0 ? raw.toFixed(1) : null;

    L.circleMarker([lat, lng], {
      radius: 12, color: '#fff', weight: 3, fillColor: color, fillOpacity: 0.95,
      className: '',
    }).addTo(map).bindPopup(`
      <div style="font-family:sans-serif;line-height:1.8;min-width:180px">
        <div style="font-weight:700;font-size:16px;font-family:monospace;color:#3b5998">${chapa}</div>
        <div style="margin-top:4px;font-size:13px">
          <span style="color:${!online ? '#9e9e9e' : moving ? '#4caf50' : '#f44336'};font-weight:700">
            ${!online ? '○ Sin señal' : moving ? '● En movimiento' : '● Detenido'}
          </span>
          ${acc ? ' &nbsp;🔑 Encendido' : ''}
        </div>
        ${moving ? `<div style="font-size:13px"><b>${spd} km/h</b></div>` : ''}
        ${volt ? `<div style="font-size:12px;color:#555">⚡ ${volt} V</div>` : ''}
      </div>
    `).openPopup();

    mapaTabInstanceRef.current = map;
    }, 120);

    return () => {
      clearTimeout(timer);
      if (mapaTabInstanceRef.current) {
        mapaTabInstanceRef.current.remove();
        mapaTabInstanceRef.current = null;
      }
    };
  }, [activeTab, gpsPos, chapa]);

  const onTabChange = (key) => {
    setActiveTab(key);
    if (key === 'mapa' && mapaTabInstanceRef.current) {
      setTimeout(() => mapaTabInstanceRef.current?.invalidateSize(), 80);
    }
  };

  const columnsOrdenes = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60,
      render: id => <span style={{ fontWeight: 700, color: hssColors.primary }}>#{id}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo', width: 140,
      render: t => <Tag icon={<FileTextOutlined />} color={t === 'Instalacion' ? 'blue' : t === 'Desinstalacion' ? 'red' : 'orange'}>{t}</Tag>,
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado', width: 130,
      render: e => {
        const c = ESTADO_CONFIG[e] || { color: 'default', icon: null, label: e };
        return <Tag icon={c.icon} color={c.color}>{c.label}</Tag>;
      },
    },
    {
      title: 'Técnico', key: 'tecnico',
      render: (_, r) => r.responsable?.nombre || '—',
    },
    {
      title: 'Fecha', key: 'fecha',
      render: (_, r) => r.fecha_creacion
        ? new Date(r.fecha_creacion).toLocaleDateString('es-PY', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '—',
    },
    {
      title: 'Observaciones', dataIndex: 'observaciones', key: 'obs',
      render: t => t || <span style={{ color: hssColors.gray400 }}>—</span>,
    },
  ];

  const columnsContratos = [
    {
      title: '#', dataIndex: 'id', key: 'id', width: 60,
      render: id => <span style={{ fontWeight: 700, color: hssColors.primary }}>#{id}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo_contrato', key: 'tipo',
      render: t => <Tag color={t === 'RASTREO' ? 'blue' : t === 'VISION' ? 'purple' : 'cyan'}>{t}</Tag>,
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado',
      render: e => <Tag color={e === 'FIRMADO' ? 'success' : e === 'BORRADOR' ? 'default' : 'error'}>{e}</Tag>,
    },
    {
      title: 'Vigencia', dataIndex: 'vigencia', key: 'vigencia',
      render: v => v || '—',
    },
    {
      title: 'Monto mensual', dataIndex: 'servicio_monto', key: 'monto',
      render: m => m || '—',
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, padding: '24px 32px' }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (!vehiculo) return null;

  const { cliente, fotos, ordenes, contratos = [] } = vehiculo;
  const tipoIcon = TIPO_ICONO[vehiculo.tipo?.toLowerCase()] || '🚗';

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ fontSize: 40 }}>{tipoIcon}</div>
            <div>
              <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: hssColors.primary, fontFamily: 'monospace', letterSpacing: 2 }}>
                {vehiculo.chapa}
                {vehiculo.alias && <span style={{ fontSize: 14, fontFamily: 'sans-serif', fontWeight: 500, color: hssColors.textSecondary, letterSpacing: 0, marginLeft: 10 }}>({vehiculo.alias})</span>}
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: hssColors.textSecondary }}>
                {vehiculo.marca} {vehiculo.modelo} {vehiculo.ano ? `· ${vehiculo.ano}` : ''} {vehiculo.color ? `· ${vehiculo.color}` : ''}
                {vehiculo.nombre_gps && <span style={{ marginLeft: 8, color: hssColors.info }}>· {vehiculo.nombre_gps}</span>}
              </p>
            </div>
          </div>
          <Space>
            <Button
              icon={<HomeOutlined />}
              onClick={() => navigate('/dashboard')}
              size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}
            >
              Inicio
            </Button>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/vehiculos')}
              size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}
            >
              Volver a Vehículos
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>
        <Tabs
          activeKey={activeTab}
          onChange={onTabChange}
          size="large"
          style={{ background: hssColors.backgroundSecondary, borderRadius: 12, padding: '0 16px', boxShadow: commonStyles.shadowMd }}
          items={[
            {
              key: 'info',
              label: <Space><CarOutlined />Información</Space>,
              children: (
                <div style={{ padding: '16px 0' }}>
                  <Row gutter={[20, 20]}>
                    <Col xs={24} lg={8}>
                      <Card
                        title={<Space><CarOutlined style={{ color: hssColors.primary }} /><span>Datos del vehículo</span></Space>}
                        style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
                      >
                        <Space direction="vertical" size={14} style={{ width: '100%' }}>
                          <div>
                            <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 2 }}>Tipo</div>
                            <Tag
                              style={{ fontSize: 14, padding: '4px 14px', borderRadius: 20, cursor: 'pointer', fontWeight: 600 }}
                              color="blue"
                              onClick={() => navigate(`/vehiculos?tipo=${vehiculo.tipo}`)}
                            >
                              {tipoIcon} {vehiculo.tipo} <span style={{ fontSize: 11, opacity: 0.7 }}>· ver todos</span>
                            </Tag>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 2 }}>Servicio</div>
                            <Tag style={{ fontSize: 14, padding: '4px 14px', borderRadius: 20, fontWeight: 600, background: SERVICIO_COLOR[vehiculo.servicio] || hssColors.gray400, color: '#fff', border: 'none' }}>
                              {vehiculo.servicio || 'Sin servicio'}
                            </Tag>
                          </div>
                          {vehiculo.chasis && (
                            <div>
                              <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 2 }}>Chasis / VIN</div>
                              <span style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>{vehiculo.chasis}</span>
                            </div>
                          )}
                          {vehiculo.dispositivos_gps?.length > 0 && (
                            <div>
                              <div style={{ fontSize: 12, color: hssColors.textSecondary, marginBottom: 2 }}>GPS instalado</div>
                              {vehiculo.dispositivos_gps.map(d => (
                                <div key={d.id}>
                                  <Tag color="blue" style={{ fontWeight: 600 }}>{d.marca} {d.modelo}</Tag>
                                  <span style={{ fontSize: 12, color: hssColors.textSecondary, fontFamily: 'monospace' }}>{d.imei}</span>
                                  {d.linea && <div style={{ fontSize: 12, color: hssColors.textSecondary }}>SIM: {d.linea.numero || d.linea.iccid}</div>}
                                </div>
                              ))}
                            </div>
                          )}
                          <Divider style={{ margin: '8px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 22, fontWeight: 700, color: hssColors.primary }}>{ordenes.length}</div>
                              <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Órdenes totales</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 22, fontWeight: 700, color: hssColors.success }}>{ordenes.filter(o => o.estado === 'COMPLETADO').length}</div>
                              <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Completadas</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 22, fontWeight: 700, color: hssColors.warning }}>{ordenes.filter(o => o.estado === 'ACTIVO').length}</div>
                              <div style={{ fontSize: 12, color: hssColors.textSecondary }}>Activas</div>
                            </div>
                          </div>
                        </Space>
                      </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Card
                        title={<Space><UserOutlined style={{ color: hssColors.info }} /><span>Propietario</span></Space>}
                        style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
                        extra={cliente && (
                          <Button size="small" type="link" onClick={() => navigate(`/clientes/${cliente.dni}`)} style={{ fontWeight: 600 }}>
                            Ver detalle →
                          </Button>
                        )}
                      >
                        {cliente ? (
                          <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: hssColors.gray50, borderRadius: 10, cursor: 'pointer', border: `1px solid ${hssColors.gray200}`, transition: commonStyles.transitionFast }}
                              onClick={() => navigate(`/clientes/${cliente.dni}`)}
                              onMouseEnter={e => { e.currentTarget.style.background = '#eef1f8'; e.currentTarget.style.borderColor = hssColors.primary; }}
                              onMouseLeave={e => { e.currentTarget.style.background = hssColors.gray50; e.currentTarget.style.borderColor = hssColors.gray200; }}
                            >
                              <Avatar size={52} style={{ background: hssColors.primary, fontSize: 20, flexShrink: 0 }}>
                                {cliente.nombre.charAt(0).toUpperCase()}
                              </Avatar>
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 16, color: hssColors.textPrimary }}>{cliente.nombre} {cliente.apellido}</div>
                                <div style={{ fontSize: 13, color: hssColors.textSecondary }}>DNI: {cliente.dni}</div>
                              </div>
                            </div>
                            {cliente.celular && <Space><PhoneOutlined style={{ color: hssColors.success }} /><a href={`tel:${cliente.celular}`} style={{ fontSize: 14 }}>{cliente.celular}</a></Space>}
                            {cliente.email && <Space><MailOutlined style={{ color: hssColors.info }} /><a href={`mailto:${cliente.email}`} style={{ fontSize: 14 }}>{cliente.email}</a></Space>}
                            {cliente.domicilio && <Space><EnvironmentOutlined style={{ color: hssColors.secondary }} /><span style={{ fontSize: 14 }}>{cliente.domicilio}</span></Space>}
                          </Space>
                        ) : (
                          <div style={{ color: hssColors.gray400, textAlign: 'center', padding: '24px 0' }}>Sin propietario</div>
                        )}
                      </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                      <Card
                        title={<Space><CalendarOutlined style={{ color: hssColors.warning }} /><span>Info adicional</span></Space>}
                        style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
                      >
                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Año</span>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>{vehiculo.ano || '—'}</span>
                          </div>
                          <Divider style={{ margin: '4px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Color</span>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{vehiculo.color || '—'}</span>
                          </div>
                          <Divider style={{ margin: '4px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Marca</span>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{vehiculo.marca}</span>
                          </div>
                          <Divider style={{ margin: '4px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Modelo</span>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{vehiculo.modelo || '—'}</span>
                          </div>
                          {vehiculo.chasis && (
                            <>
                              <Divider style={{ margin: '4px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Chasis</span>
                                <span style={{ fontWeight: 600, fontSize: 13, fontFamily: 'monospace' }}>{vehiculo.chasis}</span>
                              </div>
                            </>
                          )}
                          {vehiculo.dispositivos_gps?.length > 0 && vehiculo.dispositivos_gps.map(d => (
                            <React.Fragment key={d.id}>
                              <Divider style={{ margin: '4px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 14, color: hssColors.textSecondary }}>GPS</span>
                                <div style={{ textAlign: 'right' }}>
                                  <Tag color="blue">{d.marca} {d.modelo}</Tag>
                                  <div style={{ fontSize: 11, color: hssColors.textSecondary, fontFamily: 'monospace' }}>{d.imei}</div>
                                  {d.linea && <div style={{ fontSize: 11, color: hssColors.textSecondary }}>SIM: {d.linea.numero || d.linea.iccid}</div>}
                                </div>
                              </div>
                            </React.Fragment>
                          ))}
                          {vehiculo.plan_gps && (
                            <>
                              <Divider style={{ margin: '4px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Plan</span>
                                <Tag color={vehiculo.plan_gps === 'Internacional' ? 'purple' : 'geekblue'}>{vehiculo.plan_gps}</Tag>
                              </div>
                            </>
                          )}
                          {vehiculo.fecha_instalacion && (
                            <>
                              <Divider style={{ margin: '4px 0' }} />
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 14, color: hssColors.textSecondary }}>Instalación</span>
                                <span style={{ fontWeight: 600, fontSize: 14 }}>{vehiculo.fecha_instalacion}</span>
                              </div>
                            </>
                          )}
                        </Space>
                      </Card>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: 'ordenes',
              label: <Space><FileTextOutlined />Órdenes<Badge count={ordenes.length} style={{ background: hssColors.info }} /></Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', marginBottom: 8 }} styles={{ body: { padding: 0 } }}>
                  <Table
                    columns={columnsOrdenes}
                    dataSource={ordenes}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Sin órdenes de trabajo registradas' }}
                    onRow={() => ({ onClick: () => navigate('/ordenes'), style: { cursor: 'pointer' } })}
                  />
                </Card>
              ),
            },
            {
              key: 'contratos',
              label: <Space><FileProtectOutlined />Contratos<Badge count={contratos.length} /></Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', marginBottom: 8 }} styles={{ body: { padding: 0 } }}>
                  <Table
                    columns={columnsContratos}
                    dataSource={contratos}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    locale={{ emptyText: 'Sin contratos registrados' }}
                  />
                </Card>
              ),
            },
            {
              key: 'mapa',
              label: <Space><EnvironmentOutlined />Mapa GPS</Space>,
              children: (
                <Card
                  style={{ borderRadius: 12, border: 'none', marginBottom: 8, overflow: 'hidden' }}
                  styles={{ body: { padding: 0 } }}
                  title={
                    gpsPos ? (
                      <Space>
                        <EnvironmentOutlined style={{ color: hssColors.success }} />
                        <span>Posición en tiempo real</span>
                        {parseFloat(gpsPos.speed || 0) > 0 && (
                          <Tag color="orange"><ThunderboltOutlined /> {Math.round(parseFloat(gpsPos.speed))} km/h</Tag>
                        )}
                        {(gpsPos.params?.acc === '1' || gpsPos.params?.acc === 1 ||
                          gpsPos.params?.io239 === '1' || gpsPos.params?.io239 === 1) && (
                          <Tag color="success">Encendido</Tag>
                        )}
                      </Space>
                    ) : <Space><EnvironmentOutlined />GPS</Space>
                  }
                  extra={
                    <Button size="small" icon={<EnvironmentOutlined />} onClick={() => navigate('/mapa')}>
                      Ver todos los vehículos
                    </Button>
                  }
                >
                  {gpsPos && (parseFloat(gpsPos.lat) || parseFloat(gpsPos.lng)) ? (
                    <div ref={mapaTabDivRef} style={{ height: 480, width: '100%' }} />
                  ) : (
                    <div style={{ padding: 40, textAlign: 'center', color: hssColors.textSecondary }}>
                      <EnvironmentOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
                      Sin posición GPS disponible para este vehículo
                    </div>
                  )}
                </Card>
              ),
            },
            {
              key: 'fotos',
              label: <Space><PictureOutlined />Fotos</Space>,
              children: (
                <Card style={{ borderRadius: 12, border: 'none', marginBottom: 8 }}>
                  <Row gutter={[24, 24]}>
                    {FOTOS_CONFIG.map(({ tipo, label }) => (
                      <Col xs={12} sm={8} md={6} lg={4} key={tipo}>
                        <FotoUpload
                          label={label}
                          urlEndpoint={`/vehiculos/${chapa}/fotos`}
                          uploadExtra={{ tipo }}
                          deleteEndpoint={`/vehiculos/${chapa}/fotos/${tipo}`}
                          fotoActual={fotos[tipo]}
                          onSuccess={cargar}
                          onDelete={cargar}
                        />
                      </Col>
                    ))}
                  </Row>
                </Card>
              ),
            },
          ]}
        />
      </div>
      <Footer />
    </div>
  );
};

export default VehiculoDetalle;
